/**
 * The toast store — docs/ARCHITECTURE.md §6, recon §4.1, ADR-0016.
 *
 * Hand-written, not a dependency. The legacy build pulled in `notyf` for exactly this and
 * then fought it: `useNotification.ts` constructed a fresh `Notyf` PER HOOK CALL, so every
 * component had its own private toast stack; its colours were configured in JS and then
 * overridden by `!important` rules in globals.css. Everything that mattered about it is
 * below, in a file you can read in one sitting, themed by the same tokens as the page.
 *
 * Three behaviours are the whole contract, and all three have a test:
 *   · DEDUPE BY ID — a second toast with the same id REPLACES the first in place. Hammering
 *     Format does not stack five identical notices.
 *   · CEILING OF 3 — the oldest is evicted. A wall of toasts hides the document, and the
 *     document is the product.
 *   · PAUSE ON HOVER — a timer that expires while you are reading is a bug, not a feature.
 *
 * No React here. This is a plain observable read through `useSyncExternalStore`
 * (`useToasts.ts`), which is what keeps it testable in isolation and what stops a toast from
 * needing a provider to exist.
 */

export type ToastTone = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  /** Already localised by the caller. `ui/**` does not translate (ADR-0003). */
  readonly label: string;
  /** Platform-rendered by the caller, e.g. `'⌘Z'`. */
  readonly shortcutHint?: string;
  readonly onInvoke: () => void;
}

export interface ToastInput {
  readonly tone: ToastTone;
  /** Already localised: `app/runCommand.ts` translates `Notice.key` at the boundary. */
  readonly message: string;
  /** A stable id makes repeat notices replace rather than accumulate. */
  readonly id?: string;
  /**
   * How long the toast stays, in milliseconds. `0` means sticky.
   *
   * REQUIRED, and it did not used to be. This module carried its own
   * `DEFAULT_TOAST_DURATION_MS = 5000` for callers that omitted it, while `src/app/config.ts`
   * carried `TOAST_DEFAULT_DURATION_MS = 4000` for the caller that supplies it — two
   * "default toast duration" constants, in two layers, disagreeing. Only one was ever
   * reached, because `runCommand.show()` always fills the field in; the other was a loaded
   * gun that any future direct `hub.show` would have fired.
   *
   * Gate 2 denies `ui/` the composition root, so this layer cannot import the real number.
   * Requiring the field is the version of "declare it once" that works across that gate: the
   * hub now has no opinion to disagree with, and `src/app/config.ts` is the only place the
   * duration of a toast is decided.
   */
  readonly durationMs: number;
  readonly action?: ToastAction;
}

export interface Toast {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  readonly durationMs: number;
  readonly action?: ToastAction;
}

export interface ToastHub {
  show(input: ToastInput): string;
  dismiss(id: string): void;
  dismissAll(): void;
  /** Called while a pointer is over the region, or focus is inside it. */
  pause(): void;
  resume(): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): readonly Toast[];
}

/** docs/DESIGN.md §6.6 — the stack is capped; the oldest leaves first. */
export const TOAST_CEILING = 3;

interface Entry {
  toast: Toast;
  /** Milliseconds still owed when the countdown is running; `0` for a sticky toast. */
  remaining: number;
  startedAt: number;
  handle: ReturnType<typeof setTimeout> | null;
}

export function createToastHub(): ToastHub {
  const entries: Entry[] = [];
  const listeners = new Set<() => void>();
  let paused = false;
  let auto = 0;
  /** Cached so `useSyncExternalStore` sees a stable reference between real changes. */
  let snapshot: readonly Toast[] = [];

  const publish = (): void => {
    snapshot = entries.map((e) => e.toast);
    for (const listener of listeners) listener();
  };

  const stop = (entry: Entry): void => {
    if (entry.handle !== null) {
      clearTimeout(entry.handle);
      entry.handle = null;
    }
  };

  const start = (entry: Entry): void => {
    stop(entry);
    if (paused || entry.remaining <= 0) return;
    entry.startedAt = Date.now();
    entry.handle = setTimeout(() => {
      entry.handle = null;
      remove(entry.toast.id);
    }, entry.remaining);
  };

  const remove = (id: string): void => {
    const index = entries.findIndex((e) => e.toast.id === id);
    if (index === -1) return;
    const [entry] = entries.splice(index, 1);
    if (entry !== undefined) stop(entry);
    publish();
  };

  return {
    show(input) {
      auto += 1;
      const id = input.id ?? `toast-${String(auto)}`;
      const duration = input.durationMs;
      const toast: Toast = {
        id,
        tone: input.tone,
        message: input.message,
        durationMs: duration,
        // Spread, not `action: input.action`: `exactOptionalPropertyTypes` makes an explicit
        // `undefined` a different thing from an absent key, and it is right to.
        ...(input.action !== undefined && { action: input.action }),
      };

      const existing = entries.findIndex((e) => e.toast.id === id);
      if (existing !== -1) {
        // DEDUPE: replace in place. The toast keeps its position in the stack — a notice that
        // jumped to the bottom on every repeat would be its own kind of noise — and its
        // countdown starts over, because the message was just re-asserted.
        const entry = entries[existing];
        if (entry !== undefined) {
          entry.toast = toast;
          entry.remaining = duration;
          start(entry);
        }
      } else {
        entries.push({ toast, remaining: duration, startedAt: Date.now(), handle: null });
        const added = entries[entries.length - 1];
        if (added !== undefined) start(added);
        // CEILING: evict from the front, so the newest notice is always visible.
        while (entries.length > TOAST_CEILING) {
          const [evicted] = entries.splice(0, 1);
          if (evicted !== undefined) stop(evicted);
        }
      }

      publish();
      return id;
    },

    dismiss: remove,

    dismissAll() {
      if (entries.length === 0) return;
      for (const entry of entries) stop(entry);
      entries.length = 0;
      publish();
    },

    pause() {
      if (paused) return;
      paused = true;
      const now = Date.now();
      for (const entry of entries) {
        if (entry.handle === null) continue;
        entry.remaining = Math.max(0, entry.remaining - (now - entry.startedAt));
        stop(entry);
      }
    },

    resume() {
      if (!paused) return;
      paused = false;
      for (const entry of entries) start(entry);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      return snapshot;
    },
  };
}

/**
 * The application's one hub. `app/runCommand.ts` writes to it, `<Toaster/>` reads it.
 *
 * Tests build their own with `createToastHub()` rather than sharing this one — a store whose
 * state survives between test cases is a flaky suite waiting to happen.
 */
export const toastHub: ToastHub = createToastHub();
